import {
  Checkbox,
  FormField,
  Box,
  Tooltip,
  getInputStatus,
} from '@percent/lemonade'
import { RichTextEditor } from '@percent/domain/giving'
import styles from './waiver-section.module.scss'
import { WaiverSectionProps } from './waiver-section.types'

export function WaiverSection({
  waiver,
  setFieldValue,
  setFieldTouched,
  handleBlur,
  fieldNames = {},
  errors,
  touched,
  checkboxLabel,
  checkboxHelperText,
  textLabel,
  textDescription,
  textPlaceholder,
  contentReadOnly = false,
  hideEnabledToggle = false,
  enabledContent,
  readOnly,
  readOnlyTooltip,
}: Readonly<WaiverSectionProps>) {
  const isEnabled = hideEnabledToggle || (waiver.waiverEnabled ?? false)

  const {
    enabled: enabledFieldName = 'waiverEnabled',
    text: textFieldName = 'waiverText',
  } = fieldNames

  const waiverTextHasError = touched?.[textFieldName] && errors?.[textFieldName]
  const waiverTextErrorMessage = waiverTextHasError
    ? String(errors?.[textFieldName])
    : undefined

  const checkbox = !hideEnabledToggle ? (
    <Checkbox
      name={enabledFieldName}
      value={isEnabled ? 'true' : undefined}
      active={isEnabled}
      onChange={(e) => {
        setFieldValue(enabledFieldName, e.currentTarget.checked)
      }}
      label={checkboxLabel ?? ''}
      helperText={checkboxHelperText}
      variant="default"
      disabled={readOnly}
    />
  ) : null

  return (
    <Box className={styles.WaiverSectionWrapper}>
      {checkbox && readOnly && readOnlyTooltip ? (
        <Tooltip content={readOnlyTooltip} placement="top-start">
          {/* Native div required — Lemonade Box doesn't forwardRef, so Tooltip can't attach its ref */}
          <div className={styles.TooltipTrigger}>{checkbox}</div>
        </Tooltip>
      ) : (
        checkbox
      )}

      {isEnabled && !readOnly && enabledContent}

      {isEnabled && !readOnly && (
        <FormField
          label={textLabel}
          necessity="required"
          description={textDescription}
          status={getInputStatus(waiverTextErrorMessage)}
          statusMessage={waiverTextErrorMessage}
          disabled={contentReadOnly}
        >
          <RichTextEditor
            className={styles.WaiverEditor}
            fieldName={textFieldName}
            placeholder={textPlaceholder}
            testId="waiver-description-input"
            content={waiver.waiverText ?? ''}
            handleUpdate={(value) => setFieldValue(textFieldName, value)}
            handleBlur={(e) => {
              handleBlur(e)
              setFieldTouched(textFieldName, true)
            }}
            error={!!waiverTextErrorMessage}
            controls={[
              'bold',
              'link',
              'unlink',
              'bulletList',
              'orderedList',
              'undo',
              'redo',
            ]}
            readOnly={contentReadOnly}
          />
        </FormField>
      )}
    </Box>
  )
}
